import {
  type GetProps,
  type GetResult,
  type InputData,
  noddiAsync,
  type NoddiAsyncReturnType,
  type URLKeysTypes
} from 'noddi-async';
import { type FilterOption, type FilterOptionSource, useFilterOptionsEnabled } from 'noddi-ui-web';

declare const gatedBrand: unique symbol;

/** A query made by `useFilterOptionsGet`: the only input `toFilterOptionSource` takes. */
type FilterOptionsQuery<SelectedData> = GetResult<SelectedData> & { readonly [gatedBrand]: true };

/**
 * `noddiAsync.useGet` for what only a filter sidebar shows: option lists and facet counts. Idle until an
 * options surface is open, so a list view's own request is not queued behind every filter's options.
 * A picker that renders a filter's options outside the sidebar opens a surface with `useRequestFilterOptions`.
 *
 * Report `isLoading`, never `isPending`. While the query waits for a surface, `isLoading` is true: a picker
 * that forgot `useRequestFilterOptions` shows a skeleton that never resolves rather than an empty list that
 * looks like no results. A query the caller disabled itself stays `isLoading: false`, as with `useGet`.
 */
export const useFilterOptionsGet = <
  UrlKey extends URLKeysTypes,
  Metadata extends InputData<UrlKey>,
  SelectedData = NoddiAsyncReturnType<UrlKey, Metadata>
>(
  props: GetProps<UrlKey, Metadata, SelectedData>
): FilterOptionsQuery<SelectedData> => {
  const optionsEnabled = useFilterOptionsEnabled();
  const callerEnabled = props.queryConfig?.enabled ?? true;
  // eslint-disable-next-line no-restricted-properties -- the one gated call every filter hook goes through
  const query = noddiAsync.useGet<UrlKey, Metadata, SelectedData>({
    ...props,
    queryConfig: { ...props.queryConfig, enabled: optionsEnabled ? callerEnabled : false }
  });
  // Cached options stay usable while the gate is closed; only a query with no data yet reads as loading.
  if (optionsEnabled || callerEnabled === false || !query.isPending) {
    return query as FilterOptionsQuery<SelectedData>;
  }
  // Spread only while waiting: nothing fetches then, so reading every field costs no extra renders.
  return { ...query, isLoading: true } as FilterOptionsQuery<SelectedData>;
};

/**
 * The only way to build a `FilterOptionSource` (what `unit.multiSelect` takes): its options are the data of a
 * `useFilterOptionsGet` query (map them with `queryConfig.select`), so they always wait for the sidebar and its
 * load state is theirs. A plain `useGet`, or options from another query, is a type error.
 */
export const toFilterOptionSource = <TValue extends string | number>(
  query: FilterOptionsQuery<FilterOption<TValue>[]>,
  valueType: FilterOptionSource<TValue>['valueType'],
  search?: { search: string; onSearch: (search: string) => void }
) =>
  ({
    options: query.data ?? [],
    valueType,
    load: {
      isLoading: query.isLoading,
      isError: query.isError,
      isFetching: query.isFetching,
      onRetry: () => {
        void query.refetch();
      },
      ...search
    }
  }) as FilterOptionSource<TValue>;
